import { defineStore } from 'pinia'
import { isTokenExpired } from '../services/jwt'
import { pickProfile } from '../services/profile'
import {
  ROLE_ADMIN,
  ROLE_SUPERADMIN,
  hasAtLeast,
  resolveRole
} from '../services/roles'

// Session_Store — holds the authenticated JWT and the non-sensitive user
// profile for the current session (design §1).
//
// This is an opt-in persisted Pinia store (alongside `loginDraft`). Only the
// raw JWT and the display-safe profile subset are persisted to localStorage;
// the account password and integration secrets are NEVER stored here
// (Requirements 4.3, 11.2). Persistence lets the session survive a page reload
// (Requirement 4.1) and be visible to other tabs (Requirements 4.5, 4.6).
export const useAuthStore = defineStore('auth', {
  state: () => ({
    token: null, // raw JWT string, or null when logged out
    profile: null // display-safe user fields (UserProfile), or null
  }),
  getters: {
    // Authentication is derived solely from the token: true only when a token
    // is present AND its `exp` claim is still in the future. A null/malformed
    // token, or a missing/past `exp`, reports as unauthenticated. This is the
    // single source of truth for expiry (Requirements 4.2, 4.4, 7.3).
    isAuthenticated: (state) => !!state.token && !isTokenExpired(state.token),

    // The signed-in user's role: 'employee' | 'admin' | 'superadmin'.
    //
    // Resolved from the persisted profile's authoritative `role` field, falling
    // back to the legacy `admin` boolean for a session established before the
    // role field existed. Returns null when not authenticated, so a stale
    // profile left in localStorage after a token expired reports no role at all.
    role: (state) => {
      if (!state.token || isTokenExpired(state.token)) return null
      return resolveRole(state.profile)
    },

    // Does the session carry administrator access? True for BOTH admin and
    // superadmin — written as "at least admin" so the higher tier is never
    // accidentally excluded from administrator screens.
    //
    // Deliberately requires `isAuthenticated`: a stale persisted profile must
    // not report as an administrator once the token has expired.
    //
    // This drives NAVIGATION and UI affordances only. It is not a security
    // boundary — the server re-checks on every admin request via the `isAdmin`
    // policy, because anything held client-side can be edited in devtools.
    isAdmin: (state) => {
      if (!state.token || isTokenExpired(state.token)) return false
      return hasAtLeast(resolveRole(state.profile), ROLE_ADMIN)
    },

    // Is the session a super administrator? An exact top-rank check, for
    // capabilities deliberately withheld from ordinary administrators. Anything
    // an administrator should also be able to do must use `isAdmin` instead.
    isSuperAdmin: (state) => {
      if (!state.token || isTokenExpired(state.token)) return false
      return resolveRole(state.profile) === ROLE_SUPERADMIN
    }
  },
  actions: {
    // Store the session from a successful login response. The raw `profile`
    // (the server `user` object) is run through `pickProfile()` so only the
    // whitelisted non-sensitive fields are ever persisted (Requirements 1.5,
    // 4.3, 11.2).
    setSession ({ token, profile }) {
      this.token = token
      this.profile = pickProfile(profile)
    },
    // Idempotent logout: clears the token and profile. Calling `clear()`
    // repeatedly converges to the same logged-out state, so concurrent 403s
    // collapse to a single logged-out result (Requirement 7.1).
    clear () {
      this.token = null
      this.profile = null
    },
    // Re-read the persisted session from localStorage. Used by the cross-tab
    // `storage` event listener so a set/clear in another tab is reflected here
    // (Requirements 4.5, 4.6).
    hydrateFromStorage () {
      let persisted = null
      try {
        const raw = window.localStorage.getItem('ts-auth')
        persisted = raw ? JSON.parse(raw) : null
      } catch (e) {
        persisted = null
      }
      this.token = (persisted && persisted.token) || null
      this.profile = (persisted && persisted.profile) || null
    }
  },
  // Scope persistence to this store only, and only persist the token and the
  // non-sensitive profile — never a password or credential secret.
  persist: {
    key: 'ts-auth',
    storage: window.localStorage,
    paths: ['token', 'profile']
  }
})
